import React from 'react';
import Points from 'components/Points';
import { FinishMark } from 'components/FinishTag/FinishTag';
import { formatLanguage } from 'constants/languages';
import { formatConditionShort } from 'utils/offerErrors';
import { fromHaveFinish } from 'utils/finishes';
import type { ThreadTradeItem } from 'types/interfaces/thread';

interface Props {
  items: ThreadTradeItem[];
  /** Item ids a case reported; those rows tint orange and read "Reported". */
  affectedItemIds?: string[];
  title?: string;
}

/**
 * The package list in the rail (Decision 12): thumb, name, set, condition,
 * language, points. Point values always render through the Points pill.
 */
export const PackageList: React.FC<Props> = ({ items, affectedItemIds, title = 'Package' }) => {
  const affected = new Set(affectedItemIds ?? []);
  return (
    <section className="rail-section">
      <h4 className="rail-section-title">
        {title} <span className="rail-section-count">{items.length}</span>
      </h4>
      <ul className="rail-package">
        {items.map((item) => {
          const reported = affected.has(item.id);
          const finish = item.finish ? fromHaveFinish(item.finish) : item.foil ? 'foil' : 'nonfoil';
          return (
            <li key={item.id} data-testid="rail-package-item" className={`rail-package-item${reported ? ' reported' : ''}${item.removedAt ? ' removed' : ''}`}>
              {item.imageUrl ? (
                <img src={item.imageUrl} alt="" className="rail-package-thumb" loading="lazy" />
              ) : (
                <span className="rail-package-thumb placeholder" aria-hidden="true" />
              )}
              <span className="rail-package-body">
                <span className="rail-package-name">
                  {item.quantity > 1 && <span className="rail-package-qty">{item.quantity}x </span>}
                  {item.name}
                </span>
                <span className="rail-package-meta">
                  <span>{item.setCode.toUpperCase()}</span>
                  <span>{formatConditionShort(item.condition)}</span>
                  <span>{formatLanguage(item.language)}</span>
                  {finish !== 'nonfoil' && <FinishMark finish={finish} />}
                  {reported && <span className="rail-package-reported">Reported</span>}
                </span>
              </span>
              <Points points={item.pointValue * item.quantity} type="mini" />
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export default PackageList;
